import { useEffect, useState } from 'react'
import { Navigate, useLocation, Outlet } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { Spinner } from '../ui'
import './ProtectedRoute.css'

interface ProtectedRouteProps {
  children?: React.ReactNode
}

// Onboarding flow removed - all users go directly to dashboard
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation()
  const {
    isAuthenticated,
    isInitialized,
    isLoading,
    initAuth
  } = useAuthStore()

  // Max loading timeout - 8 seconds before showing error/redirect
  const [loadingTimeout, setLoadingTimeout] = useState(false)

  useEffect(() => {
    if (!isInitialized) {
      initAuth()
    }
  }, [isInitialized, initAuth])

  // Set timeout to prevent infinite loading
  useEffect(() => {
    if (!isInitialized || isLoading) {
      const timer = setTimeout(() => {
        setLoadingTimeout(true)
      }, 8000)
      return () => clearTimeout(timer)
    }
  }, [isInitialized, isLoading])

  // Show loading while initializing auth (max 8 seconds)
  if ((!isInitialized || isLoading) && !loadingTimeout) {
    return (
      <div className="auth-loading">
        <div className="auth-loading-content">
          <div className="auth-loading-logo">
            <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M16 2L30 28H2L16 2Z"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M16 10L24 24H8L16 10Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <Spinner size="lg" />
          <p>Loading APEX...</p>
        </div>
      </div>
    )
  }

  // Loading timed out - redirect to login
  if (loadingTimeout && (!isInitialized || isLoading)) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }

  // Render children or outlet
  return children ? <>{children}</> : <Outlet />
}

export default ProtectedRoute
